import React from "react";
import { motion } from "framer-motion";
import { Flame, MessageCircle, Flag, Heart } from "lucide-react";
import { IMAGES } from "@/data/content";
import { optimizeImg } from "@/lib/img";

const challenges = [
  { icon: Flag, title: "30-Day Shred", meta: "2,481 joined", pct: 72 },
  { icon: Flame, title: "10K Step Streak", meta: "5,902 joined", pct: 58 },
  { icon: Heart, title: "Clean Eating Week", meta: "1,204 joined", pct: 40 },
];

export const Community = () => {
  return (
    <section id="community" data-testid="community-section" className="relative py-24 md:py-32 bg-charcoal/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden border border-white/10 order-2 lg:order-1"
        >
          <img src={optimizeImg(IMAGES.community, 1000)} alt="Fitness community training" loading="lazy" decoding="async" className="w-full h-[480px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-4 flex items-center gap-4">
            <MessageCircle className="w-8 h-8 text-vitality shrink-0" />
            <div>
              <p className="font-heading text-lg uppercase tracking-tight">In This Pack, No One Trains Alone</p>
              <p className="text-white/60 text-sm">Real-time messaging, leaderboards & accountability groups.</p>
            </div>
          </div>
        </motion.div>

        <div className="order-1 lg:order-2">
          <span className="text-xs font-bold tracking-[0.25em] uppercase text-vitality">Community & Challenges</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight mt-4 leading-tight">
            Stronger Together
          </h2>
          <p className="text-white/60 mt-4 text-lg leading-relaxed">
            Stay motivated with fun challenges and a social feed to share your wins.
            Connect with friends, cheer each other on and message your coach — competition or community, you're never in it alone.
          </p>

          <div className="mt-8 space-y-4">
            {challenges.map((c, i) => (
              <motion.div
                key={c.title}
                data-testid={`challenge-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-charcoal border border-white/10 rounded-xl p-4 hover:border-vitality/40 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-vitality/10 flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-vitality" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-heading text-lg uppercase tracking-tight">{c.title}</span>
                      <span className="text-xs text-white/50">{c.meta}</span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full bg-vitality" style={{ width: `${c.pct}%` }} />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <a
            href="#pricing"
            data-testid="join-community-button"
            className="inline-flex items-center mt-8 bg-transparent border border-white/20 text-white font-bold uppercase tracking-wider text-sm px-7 py-3 rounded-xl hover:border-vitality hover:text-vitality transition-colors"
          >
            Join the AlphaPulse Community
          </a>
        </div>
      </div>
    </section>
  );
};
